import { BrowserRouter as Router, useLocation } from 'react-router-dom';
import { useState, useEffect } from 'react';
import PublicRoutes from './PublicRoutes';
import MemberRoutes from './MemberRoutes';
import { useSelector } from 'react-redux';
import AnalystRoutes from './AnalystRoutes';
import RegulatorRoutes from './RegulatorRoutes';
import GoverningBodyRoutes from './GoverningBodyRoutes';
import { analystChartTitles, memberAnalystChartTitles } from '../utils/reuse';

const getTitleFromPathname = (pathname, role, search) => {
    if (pathname.startsWith('/alert-details/')) {
        const segments = pathname.split('/').filter(Boolean);
        const alertId = segments[1];
        return alertId ? `IBIA | ${alertId}` : 'IBIA | Alert Details';
    }

    if (pathname === '/my-analytics-details') {
        const searchParams = new URLSearchParams(search);
        const type = searchParams.get('type');
        let chartTitle = '';
        if (type) {
            if (role === 'analyst') {
                chartTitle = analystChartTitles[type]?.title;
            } else {
                chartTitle = memberAnalystChartTitles[type]?.title;
            }
        }
        return `IBIA | ${chartTitle ? `${chartTitle} | ` : ''}${role === 'analyst' ? 'Member Analytics Details' : 'My Analytics Details'}`;    }

    const pathMap = {
        '/home': 'Home',
        '/alerts': 'Alerts',
        '/open-alerts': 'Open Alerts',
        '/evaluating-alerts': 'Evaluating Alerts',
        '/suspicious-alerts': 'Suspicious Alerts',
        '/my-alerts': 'My Alerts',
        '/create-alert': 'Create Alert',
        '/my-analytics': role === 'analyst' ? 'Member analytics' : 'My Analytics',
        '/case-updates': 'Case Updates',
        '/news': 'News',
        '/news-category': 'News Category',
        '/news-categories': 'News Categories',
        '/news-details': 'News Details',
        '/preview-news': 'Preview News',
        '/edit-details': 'Edit News',
        '/create-news': 'Create News',
        '/sporting-sanction': 'Sporting Sanction',
        '/sanction-details': 'Sanction Details',
        '/weekly-integrity': 'Weekly Integrity Preview',
        '/external-links': 'External Links',
        '/profile-details': 'Profile Details',
        '/sports': 'Sports',
        '/help': 'Help Guide',
        '/conversations': 'Conversations',
        '/user-management': 'User Management',
        '/all-users': 'All Users',
        '/audit-log': 'Audit Log',
        '/reports': 'Reports',
        '/create-report': 'Create Report',
        '/report-detail': 'View Report',
        '/preview-report': 'Preview Report',
        '/download-report': 'Download Report',
        '/edit-report': 'Edit Report',
        '/tags': 'Tags',
        '/betting-data': 'Betting Data',
        '/player-analytics': 'Player Analytics',
        '/anomalies': 'Anomalies',
        '/baseline-configuration': 'Baseline Configuration',
        '/page-not-found': 'Page Not Found',
        '/login': 'Login',
        '/otp': 'OTP',
        '/forgot-password': 'Forgot Password',
        '/set-password': 'Set Password',
        '/reset-password': 'Reset Password'
    };

    if (pathMap[pathname]) {
        return `IBIA | ${pathMap[pathname]}`;
    }

    if (pathname.startsWith('/bet-details/')) {
        const segments = pathname.split('/').filter(Boolean);
        const betId = segments[1];
        return betId ? `IBIA | Bet Details | ${betId}` : 'IBIA | Bet Details';
    }
    if (pathname.startsWith('/player-analytics/')) {
        const segments = pathname.split('/').filter(Boolean);
        const playerId = segments[1];
        return playerId ? `IBIA | Player Analytics | ${playerId}` : 'IBIA | Player Analytics';
    }

    const segments = pathname.split('/').filter(Boolean);
    if (segments.length > 0) {
        const firstSegment = `/${segments[0]}`;
        if (pathMap[firstSegment]) {
            return `IBIA | ${pathMap[firstSegment]}`;
        }
        const title = segments[0]
            .split('-')
            .map(word => word.charAt(0).toUpperCase() + word.slice(1))
            .join(' ');
        return `IBIA | ${title}`;
    }

    return 'IBIA | International Betting Integrity Association';
};

const TitleManager = () => {
    const location = useLocation();
    const role = useSelector(state => state.auth.role);
    useEffect(() => {
        const title = getTitleFromPathname(location.pathname, role, location.search);
        if (title) {
            const searchParams = new URLSearchParams(location.search);
            const id = searchParams.get('id');
            const tagName = searchParams.get('tagName');
            if (tagName) {
                document.title = `${title} | ${tagName}`;
            } else if (id) {
                document.title = `${title} | ${id}`;
            } else {
                document.title = title;
            }
        }
    }, [location.pathname, location.search, role]);

    return null;
};

const Navigation = () => {
    const isManagedMember = useSelector(state => state.auth.role) === 'managedmember';
    const isMember = useSelector(state => state.auth.role) === 'member';
    const isAnalyst = useSelector(state => state.auth.role) === 'analyst';
    const isRegulator = useSelector(state => state.auth.role) === 'regulator';
    const isGoverningBody = useSelector(state => state.auth.role) === 'governingbody';
    const isAuthenticated = useSelector(state => state.auth.isLoggedIn);

    const [activeIndex, setActiveIndex] = useState(0)
    const handleTabChange = (index) => {
        setActiveIndex(index)
    }
    const originalWarn = console.warn;
    console.warn = (...args) => {
        if (args?.length && args[0] && typeof args[0] === 'string' && args[0].includes('React Router Future Flag Warning')) {
            return; // Don't log this specific warning
        }
        originalWarn(...args);
    };
    return (
        <Router>
            <TitleManager />
            {!isAuthenticated && <PublicRoutes />}
            {isAnalyst && <AnalystRoutes />}
            {(isMember || isManagedMember) && <MemberRoutes handleTabChange={handleTabChange} setActiveIndex={setActiveIndex} activeIndex={activeIndex} />}
            {isRegulator && <RegulatorRoutes />}
            {isGoverningBody && <GoverningBodyRoutes />}
        </Router>
    );


}

export default Navigation;